(function () {
  const { Component } = window;

  const style = `
    :host {
    --filterGroupTextColor: var(--neutral-100, #e7e7e7);

    --filterGroupHeaderFont: var(--font-heading-sm, bold 16px/1.3 sans-serif);

    --filterGroupBorderColor: #ffffff14;
    --filterGroupFocusRing: var(--blue-400, #3478c1);
    --filterGroupChevronColor: var(--neutral-100, #e7e7e7);

      display: block;
      box-sizing: border-box;
    }

    :host([hidden]) { display: none; }

    .facet {
      border-bottom: 1px solid var(--filterGroupBorderColor);
      padding: 20px 0;
    }

    .header {
      appearance: none;
      background: none;
      border: none;
      color: var(--filterGroupTextColor);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font: var(--filterGroupHeaderFont);
      padding: 0;
      width: 100%;
    }

    .header:focus-visible {
      outline: 2px solid var(--filterGroupFocusRing);
      outline-offset: 2px;
      border-radius: 4px;
    }

    .chevron {
      border: solid var(--filterGroupChevronColor);
      border-width: 0 2px 2px 0;
      display: inline-block;
      padding: 4px;
      transition: transform 0.2s ease;
      transform: rotate(45deg);
    }

    :host([expanded]) .chevron {
      transform: rotate(-135deg);
    }

    :host(:not([is-expandable])) .chevron {
      display: none;
    }

    :host(:not([is-expandable])) .header {
      cursor: default;
      pointer-events: none;
    }

    .options {
      display: none;
      flex-wrap: wrap;
      gap: 8px;
      padding-top: 16px;
    }

    :host([expanded]) .options,
    :host(:not([is-expandable])) .options {
      display: flex;
    }

    @media (prefers-reduced-motion: reduce) {
      .chevron { transition: none; }
    }
  `;

  const template = document.createElement('template');
  template.innerHTML = `
    <style>${style}</style>
    <div class="facet">
      <button class="header" type="button" aria-expanded="false">
        <span class="label"></span>
        <span class="chevron"></span>
      </button>
      <div class="options" role="radiogroup" aria-label="">
        <slot></slot>
      </div>
    </div>
  `;

  class FilterGroup extends Component {
    static get observedAttributes() {
      return ['label', 'expanded', 'is-expandable'];
    }

    static get propsCamelConventionEnabled() {
      return true;
    }

    constructor() {
      super();
      this.initBool(['expanded', 'is-expandable']);
    }

    connectedCallback() {
      this._headerBtn = this.select('.header');
      this._labelEl = this.select('.label');
      this._optionsEl = this.select('.options');

      this.attachEvent(this._headerBtn, 'click', this._onHeaderClick);
      this.addEventListener('filter-facet-change', this._onFacetChange);

      this._syncLabel();
      this._syncExpanded();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListener('filter-facet-change', this._onFacetChange);
    }

    template() {
      return template;
    }

    get selectedValue() {
      const selected = this.querySelector('filter-facet[selected]');
      return selected ? selected.getAttribute('value') : null;
    }

    clearSelection() {
      const facets = this.querySelectorAll('filter-facet[selected]');
      facets.forEach(opt => opt.removeAttribute('selected'));
    }

    _onHeaderClick() {
      if (!this['is-expandable']) {
        return;
      }
      this.expanded = !this.expanded;
      this._syncExpanded();
    }

    _onFacetChange(e) {
      const { value, selected } = e.detail;

      // Single-select: deselect all others in this group
      if (selected) {
        const facets = this.querySelectorAll('filter-facet');
        facets.forEach((facet) => {
          if (facet.getAttribute('value') !== value) {
            facet.removeAttribute('selected');
          }
        });
      }

      this.dispatchEvent(new CustomEvent('filter-group-change', {
        bubbles: true,
        composed: true,
        detail: {
          group: this.label,
          value: selected ? value : null
        }
      }));
    }

    _attributeLabel() {
      this._syncLabel();
    }

    _attributeExpanded() {
      this._syncExpanded();
    }

    _attributeIsExpandable() {
      this._syncExpanded();
    }

    _syncLabel() {
      if (!this._labelEl) {
        return;
      }
      const label = this.getAttribute('label') || '';
      this._labelEl.textContent = label;
      this._optionsEl.setAttribute('aria-label', label);
    }

    _syncExpanded() {
      if (!this._headerBtn) {
        return;
      }
      if (!this['is-expandable']) {
        this._headerBtn.removeAttribute('aria-expanded');
        return;
      }
      const expanded = this.expanded === true;
      this._headerBtn.setAttribute('aria-expanded', String(expanded));
    }
  }

  if (!window.customElements.get('filter-group')) {
    window.customElements.define('filter-group', FilterGroup);
  }

  /* istanbul ignore next */
  if (typeof module !== 'undefined') {
    module.exports = FilterGroup;
  }
})();
